/* Opponent profiles: the kinds of player a collection knows about, and what a chart written
   against one paints at the table.

   A chart answers its question for an opponent it knows nothing about. A profile names an
   opponent worth answering it differently for — a nit, an aggro — and a variation is a chart
   of the same spot written against that profile. The list belongs to the collection rather
   than to the user, because the meaning of a variation has to travel with the chart: a
   published collection is read by people who never saw the author's preferences. */

import { z } from "./zod";
import { SEATS } from "./seats";
import { seatsFor } from "./rules";
import type { SpotPlace } from "./spot";

/* The colour has one job: to be told apart from the others on the felt, from across the
   table, in every theme. Eight hues that do that are chosen once, in CSS, and a profile
   stores the name of one — a closed palette rather than a colour field. */
export const PROFILE_COLORS = [
  "red",
  "orange",
  "amber",
  "green",
  "teal",
  "blue",
  "violet",
  "pink",
] as const;
export type ProfileColor = (typeof PROFILE_COLORS)[number];

/** A kind of opponent: what the author calls it, what it looks like, and what it means. */
export interface Opponent {
  /** The name slugged the day the profile was made. Never changes: a rename is a rename. */
  id: string;
  name: string;
  color: ProfileColor;
  /** What the app shows on hover, so a button reading `NIT` says what its author meant. */
  note: string;
}

export const PROFILE_NAME_MAX = 30;
export const PROFILE_NOTE_MAX = 160;
export const PROFILES_MAX = 24;
export const PROFILE_ID_MAX = 40;

/* An id goes into a spot key after a `+`, so it holds only what a key holds. */
const ID_SHAPE = /^[a-z0-9][a-z0-9-]*$/;

export const opponentSchema = z.strictObject({
  id: z.string().max(PROFILE_ID_MAX).regex(ID_SHAPE, "not a profile id"),
  name: z.string().trim().min(1, "Give the profile a name.").max(PROFILE_NAME_MAX),
  color: z.enum(PROFILE_COLORS),
  note: z.string().trim().max(PROFILE_NOTE_MAX).default(""),
});

export const opponentsSchema = z
  .array(opponentSchema)
  .max(PROFILES_MAX, `A collection holds at most ${PROFILES_MAX} opponent profiles.`)
  .refine(
    (list) => new Set(list.map((o) => o.id)).size === list.length,
    "Two profiles cannot share an id.",
  );

/** The four every new collection is born with. A copy takes its source's list as it is. */
export const DEFAULT_OPPONENTS: readonly Opponent[] = [
  {
    id: "nit",
    name: "NIT",
    color: "blue",
    note: "Plays few hands and raises fewer. A raise means it, a call is strong: open wider into it, fold to its aggression.",
  },
  {
    id: "super-nit",
    name: "Super NIT",
    color: "violet",
    note: "Only the very top of the range, ever. Its 3-bet is QQ+ and AK: give it nothing without a premium.",
  },
  {
    id: "passive-fish",
    name: "Passive Fish",
    color: "green",
    note: "Calls far too much and rarely raises. Open and 3-bet wider for value, and do not bluff it.",
  },
  {
    id: "aggro",
    name: "Aggro",
    color: "red",
    note: "Raises and 3-bets wide, and fires back. Open tighter in front of it; call and 4-bet more against it.",
  },
];

/**
 * An id from a name, the day the profile is made: `Passive Fish` -> `passive-fish`. A name
 * with nothing an id can hold — punctuation, another script — still gets one, because the
 * id is what the charts are keyed on and it can never be empty. `taken` is the ids already
 * handed out, and a clash gets a number.
 */
export function slugOf(name: string, taken: ReadonlySet<string> = new Set()): string {
  const stem =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, PROFILE_ID_MAX) || "profile";
  for (let n = 1; ; n++) {
    const id = n === 1 ? stem : `${stem}-${n}`.slice(0, PROFILE_ID_MAX);
    if (!taken.has(id)) return id;
  }
}

/** Whatever was stored, by this build or another: the entries that are profiles. */
export function readOpponents(raw: unknown): Opponent[] {
  const read = opponentsSchema.safeParse(raw);
  if (read.success) return read.data;
  if (!Array.isArray(raw)) return [];
  // One bad entry in a list is not a reason to lose the rest: a collection written by a build
  // that knows a colour this one does not still has its other profiles.
  const out: Opponent[] = [];
  const taken = new Set<string>();
  for (const entry of raw) {
    const one = opponentSchema.safeParse(entry);
    if (!one.success || taken.has(one.data.id) || out.length >= PROFILES_MAX) continue;
    taken.add(one.data.id);
    out.push(one.data);
  }
  return out;
}

/** The profile with this id, or null: a chart may name one the collection no longer holds. */
export const opponentOf = (
  list: readonly Opponent[],
  id: string | null | undefined,
): Opponent | null => (id ? (list.find((o) => o.id === id) ?? null) : null);

/** What a chart's profile is shown as. An id nothing answers to is shown as it is stored, so
    a variation whose profile was removed still says which one it was written against. */
export const opponentName = (list: readonly Opponent[], id: string): string =>
  opponentOf(list, id)?.name ?? id;

/**
 * How a variation is named wherever the question a chart answers is stated: the spot title,
 * the story, the line above the trainer's table, the recap. One phrase, so the four places
 * cannot drift apart. Empty for the chart that is against nobody in particular.
 */
export const versus = (list: readonly Opponent[], id: string | null | undefined): string =>
  id ? `vs ${opponentName(list, id)}` : "";

/* ---- who wears the colour ---- */

/**
 * The seats a variation paints, read off the spot rather than stored.
 *
 * One profile per chart, and it says what kind of table this is played at rather than which
 * seat is the nit. So the action decides: the seats it names are the opponents it is about,
 * and on an RFI — which names nobody — it is about everyone still to act behind hero. The
 * aggressive player in the blinds that makes the button open fewer hands is exactly what the
 * colour on the blinds says.
 *
 * Hero is never painted, and neither is a seat that folded before the action reached hero.
 */
export type VillainPlace = Pick<SpotPlace, "action" | "hero" | "seats"> &
  Partial<Pick<SpotPlace, "gameType">>;

export function villainSeats(spot: VillainPlace): string[] {
  const named = (spot.seats ?? []).filter((s) => s && s !== spot.hero);
  if (spot.action !== "RFI") return [...new Set(named)];
  const order = spot.gameType ? seatsFor(spot.gameType) : (SEATS as readonly string[]);
  const at = order.indexOf(spot.hero);
  return at < 0 ? [] : [...order.slice(at + 1)];
}
